/**
 * Streaming progress contract for POST /api/list/photo.
 *
 * The extraction path (image / scanned-PDF / text-PDF) is slow — it runs one
 * or two sequential LLM calls with retries — so instead of a single blocking
 * JSON response, the server streams newline-delimited JSON (NDJSON) events as
 * each stage begins, letting the client show real progress. Shared by both the
 * route handler and the client stream reader so the two can't drift.
 *
 * Note: only the extraction path streams. Pre-extraction validation errors are
 * still returned as a single JSON response with the appropriate HTTP status,
 * before streaming begins.
 */

/** Content-Type used for the streamed extraction response. */
export const LIST_PHOTO_STREAM_CONTENT_TYPE = "application/x-ndjson";

/**
 * The real server-side stages of extraction, emitted as each begins:
 * - `reading`: file has been read/parsed; handing off to the model.
 * - `extracting`: the extraction LLM call (vision or text) is running.
 * - `interpreting`: the enrichment LLM call is normalizing the raw items.
 */
export type ExtractionStage = "reading" | "extracting" | "interpreting";

/** A single extracted item, mirrors the non-streaming response shape. */
export interface ListPhotoStreamItem {
  searchTerm: string;
  quantity: number;
}

/** A stage began. */
export interface ListPhotoStageEvent {
  type: "stage";
  stage: ExtractionStage;
}

/** Extraction finished successfully with the final item list. */
export interface ListPhotoCompleteEvent {
  type: "complete";
  items: ListPhotoStreamItem[];
  /**
   * Number of items whose quantity exceeded the allowed maximum and was
   * clamped down. Present (and > 0) only when clamping occurred, so the client
   * can warn the user their quantities were capped.
   */
  clampedCount?: number;
}

/**
 * Extraction failed after the stream had already committed to 200.
 * `reason` mirrors the non-streaming failure classification so the client can
 * show the same "try again" vs "different file" guidance.
 */
export interface ListPhotoErrorEvent {
  type: "error";
  reason: "transient" | "unreadable";
  message: string;
}

export type ListPhotoStreamEvent =
  | ListPhotoStageEvent
  | ListPhotoCompleteEvent
  | ListPhotoErrorEvent;
